import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { useToast } from '@shared/hooks/use-toast';
import { CheckCircle2, File, Loader2, Trash2, Upload, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkInboxCourierFiles, useCouriersDropdown } from '../../hooks';
import { BulkInboxCourierFilesResponse } from '../../models';

const MAX_FILES = 50;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT = ['.csv', '.txt', '.xls', '.xlsx'];

function getExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

interface BulkInboxCourierFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BulkInboxCourierFilesModal = ({ isOpen, onClose }: BulkInboxCourierFilesModalProps) => {
  const { t } = useTranslation('codCourierFiles');
  const [files, setFiles] = useState<File[]>([]);
  const [courierId, setCourierId] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkInboxCourierFilesResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { bulkUploadAsync, isBulkUploading } = useBulkInboxCourierFiles();
  const { toast } = useToast();
  const { couriers, isLoading: isLoadingCouriers } = useCouriersDropdown();

  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setCourierId('');
      setDragActive(false);
      setUploadResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  const courierOptions = useMemo(
    () =>
      couriers.map((courier) => ({
        label: courier.name,
        value: String(courier.id),
      })),
    [couriers],
  );

  const validateAndAppend = (incoming: File[]) => {
    const room = MAX_FILES - files.length;
    if (room <= 0) {
      toast({
        title: t('bulk.error.title', 'Error en la carga masiva'),
        description: t('bulk.error.max_files', 'Máximo {{max}} archivos por envío', { max: MAX_FILES }),
        variant: 'destructive',
      });
      return;
    }

    const accepted: File[] = [];
    for (const f of incoming) {
      const ext = getExtension(f.name);
      if (!ALLOWED_EXT.includes(ext)) {
        toast({
          title: t('bulk.error.invalid_type_title', 'Tipo no permitido'),
          description: t('bulk.error.invalid_type', '{{name}}: usa CSV, TXT, XLS o XLSX', { name: f.name }),
          variant: 'destructive',
        });
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast({
          title: t('bulk.error.size_title', 'Archivo demasiado grande'),
          description: t('bulk.error.too_large', '{{name}} supera 10 MB', { name: f.name }),
          variant: 'destructive',
        });
        continue;
      }
      accepted.push(f);
    }

    const toAdd = accepted.slice(0, room);
    if (accepted.length > room) {
      toast({
        title: t('bulk.error.title', 'Error en la carga masiva'),
        description: t('bulk.error.max_files', 'Máximo {{max}} archivos por envío', { max: MAX_FILES }),
        variant: 'destructive',
      });
    }
    if (toAdd.length > 0) {
      setFiles((prev) => [...prev, ...toAdd]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    validateAndAppend(Array.from(list));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (isBulkUploading) return;
    const list = e.dataTransfer.files;
    if (!list?.length) return;
    validateAndAppend(Array.from(list));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast({
        title: t('bulk.error.title', 'Error en la carga masiva'),
        description: t('bulk.error.files_required', 'Selecciona al menos un archivo'),
        variant: 'destructive',
      });
      return;
    }
    if (!courierId) {
      toast({
        title: t('bulk.error.title', 'Error en la carga masiva'),
        description: t('upload.error.courier_required', 'Debe seleccionar un courier'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await bulkUploadAsync({
        courier_id: parseInt(courierId, 10),
        files,
      });
      setUploadResult(result);
    } catch {
      /* toast en hook */
    }
  };

  const handleCloseAndReset = () => {
    onClose();
  };

  const removeAt = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isBulkUploading) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[560px]" data-testid="bulk-inbox-courier-modal">
        {uploadResult ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                {t('bulk.result.title', 'Archivos enviados a la bandeja')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'bulk.result.description',
                  'Los ficheros quedan en la carpeta de entrada para que el sistema los procese después.',
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 space-y-4 max-h-[min(60vh,420px)] overflow-y-auto">
              {uploadResult.uploaded.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {t('bulk.result.uploaded_title', 'Subidos correctamente')} ({uploadResult.uploaded.length})
                  </p>
                  <ul className="text-sm space-y-1.5 border border-border rounded-lg p-3 bg-muted/30">
                    {uploadResult.uploaded.map((u, idx) => (
                      <li key={`${u.stored_as}-${idx}`} className="flex gap-2 items-start">
                        <File className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate" title={u.original_name}>
                            {u.original_name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate" title={u.path}>
                            → {u.stored_as}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uploadResult.failed.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-destructive" />
                    {t('bulk.result.failed_title', 'No se pudieron subir')} ({uploadResult.failed.length})
                  </p>
                  <ul className="text-sm space-y-2 border border-destructive/30 rounded-lg p-3 bg-destructive/5">
                    {uploadResult.failed.map((f, idx) => (
                      <li key={`${f.original_name}-${idx}`}>
                        <p className="font-medium text-foreground">{f.original_name}</p>
                        <p className="text-xs text-muted-foreground">{f.message}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={handleCloseAndReset}>{t('upload.result.close', 'Cerrar')}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('bulk.modal_title', 'Carga masiva a bandeja COD')}</DialogTitle>
              <DialogDescription>
                {t(
                  'bulk.modal_description',
                  'Sube uno o varios ficheros a la carpeta de entrada. No se importan en base de datos en este paso.',
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <p className="text-xs text-muted-foreground">
                {t('bulk.hint_limits', 'Entre 1 y 50 archivos, máx. 10 MB cada uno. Formatos: CSV, TXT, XLS, XLSX.')}
              </p>

              <div className="space-y-2">
                <Label>{t('upload.courier', 'Courier')}</Label>
                <Combobox
                  options={courierOptions}
                  value={courierId}
                  setValue={setCourierId}
                  placeholder={
                    isLoadingCouriers
                      ? t('upload.loading_couriers', 'Cargando couriers...')
                      : t('upload.select_courier', 'Seleccionar courier')
                  }
                  translations={{
                    notFound: t('upload.no_couriers_found', 'No se encontraron couriers'),
                  }}
                  disabled={isBulkUploading || isLoadingCouriers}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('bulk.files_label', 'Archivos')}</Label>
                <div
                  onClick={() => !isBulkUploading && fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isBulkUploading) setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                  }}
                  className={`
                    relative flex flex-col items-center justify-center w-full min-h-[120px] border-2 border-dashed rounded-lg cursor-pointer transition-colors
                    ${dragActive ? 'border-primary bg-primary/5' : 'border-card-border bg-card-bg hover:border-primary/50'}
                    ${isBulkUploading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".csv,.txt,.xls,.xlsx"
                    onChange={handleFileChange}
                    disabled={isBulkUploading}
                    className="hidden"
                  />
                  <Upload className={`h-8 w-8 mb-2 ${dragActive ? 'text-primary' : 'text-muted-foreground'}`} />
                  <p className="text-sm font-medium text-foreground text-center px-2">
                    {t('bulk.click_or_add', 'Haz clic para elegir archivos o arrástralos aquí')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('bulk.selected_count', '{{count}} / {{max}} archivos', { count: files.length, max: MAX_FILES })}
                  </p>
                </div>

                {files.length > 0 && (
                  <ul className="max-h-40 overflow-y-auto border border-border rounded-md divide-y divide-border text-sm">
                    {files.map((f, index) => (
                      <li
                        key={`${f.name}-${f.size}-${index}`}
                        className="flex items-center justify-between gap-2 px-2 py-1.5"
                      >
                        <span className="truncate text-foreground" title={f.name}>
                          {f.name}
                        </span>
                        <span className="text-muted-foreground shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAt(index);
                          }}
                          disabled={isBulkUploading}
                          aria-label={t('bulk.remove_file', 'Quitar archivo')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => !isBulkUploading && onClose()} disabled={isBulkUploading}>
                {t('upload.cancel', 'Cancelar')}
              </Button>
              <Button onClick={handleUpload} disabled={isBulkUploading || !courierId || files.length === 0}>
                {isBulkUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('bulk.uploading', 'Subiendo...')}
                  </>
                ) : (
                  t('bulk.confirm', 'Enviar a bandeja')
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
